import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { catchError, of } from 'rxjs';
import {
  ALL_POOL_NAMES,
  CostingPoolsProxyService,
  PoolName,
  PoolStatus,
  PoolStatusEntry,
} from '../../../../proxies/costing-pools.proxy.service';

interface BannerRow {
  name: PoolName;
  isReady: boolean;
  status: PoolStatus | null;
}

/**
 * Period-level pool readiness banner. Surfaces, at a glance, which cost
 * components on the current dashboard are sourced from a real run vs. read-
 * time standards. Uses <c>GET /api/v1/costing-pools/status/{year}/{month}</c>
 * — the same call the Pool Control panel makes — so the two stay in sync.
 *
 * Renders one chip per pool, green = Ready (Real), grey = not Ready (Standard
 * fallback), plus a summary headline. Intended to live near the top of the
 * Cost Analysis page where the user can see it without expanding any panel.
 */
@Component({
  selector: 'app-pool-readiness-banner',
  templateUrl: './pool-readiness-banner.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PoolReadinessBannerComponent {
  private readonly proxy = inject(CostingPoolsProxyService);

  readonly year = input.required<number>();
  readonly month = input.required<number>();

  readonly loading = signal<boolean>(false);
  readonly rows = signal<BannerRow[]>([]);

  readonly readyCount = computed(() => this.rows().filter((r) => r.isReady).length);
  readonly totalCount = computed(() => this.rows().length);
  readonly missingNames = computed(() =>
    this.rows()
      .filter((r) => !r.isReady)
      .map((r) => r.name),
  );

  /**
   * Three-state summary that drives the headline + accent color:
   * <c>"Real"</c> = every pool ready,
   * <c>"Mixed"</c> = some ready / some not,
   * <c>"Standard"</c> = none ready.
   */
  readonly mode = computed<'Real' | 'Mixed' | 'Standard'>(() => {
    const ready = this.readyCount();
    const total = this.totalCount();
    if (total === 0) {
      return 'Standard';
    }
    if (ready === total) {
      return 'Real';
    }
    if (ready === 0) {
      return 'Standard';
    }
    return 'Mixed';
  });

  constructor() {
    effect(
      () => {
        this.year();
        this.month();
        untracked(() => this.load());
      },
      { allowSignalWrites: true },
    );
  }

  load(): void {
    const y = this.year();
    const m = this.month();
    this.loading.set(true);
    this.proxy
      .getStatus(y, m)
      .pipe(catchError(() => of({ success: false, data: null, errorMessage: null })))
      .subscribe((resp) => {
        if (resp.success && resp.data) {
          this.rows.set(resp.data.pools.map((p) => this.toRow(p)));
        } else {
          this.rows.set(ALL_POOL_NAMES.map((n) => ({ name: n, isReady: false, status: null })));
        }
        this.loading.set(false);
      });
  }

  chipClasses(row: BannerRow): string {
    const base = 'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium';
    return row.isReady
      ? `${base} bg-[var(--p-green-100)] text-[var(--p-green-800)]`
      : `${base} bg-[var(--p-surface-100)] text-[var(--p-surface-700)]`;
  }

  bannerClasses(): string {
    const base = 'flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 text-sm';
    switch (this.mode()) {
      case 'Real':
        return `${base} border-[var(--p-green-200)] bg-[var(--p-green-50)]`;
      case 'Mixed':
        return `${base} border-[var(--p-amber-200)] bg-[var(--p-amber-50)]`;
      case 'Standard':
        return `${base} border-[var(--p-surface-200)] bg-[var(--p-surface-50)]`;
    }
  }

  bannerIcon(): string {
    switch (this.mode()) {
      case 'Real':
        return 'pi pi-check-circle text-[var(--p-green-700)]';
      case 'Mixed':
        return 'pi pi-info-circle text-[var(--p-amber-700)]';
      case 'Standard':
        return 'pi pi-info-circle text-[var(--p-text-muted-color)]';
    }
  }

  bannerTitle(): string {
    switch (this.mode()) {
      case 'Real':
        return 'Real cost — every pool is ready and feeding actual rates.';
      case 'Mixed':
        return 'Mixed cost — some components are real, others fall to standards at read time.';
      case 'Standard':
        return 'Standard cost — no real-cost pools are ready; every value is sourced from FabricColorStandardCost.';
    }
  }

  trackByName(_index: number, row: BannerRow): string {
    return row.name;
  }

  private toRow(entry: PoolStatusEntry): BannerRow {
    return {
      name: entry.name,
      isReady: entry.status === PoolStatus.Ready,
      status: entry.status ?? null,
    };
  }
}
